import { FullScreenPanel } from '@/features/experiences/components/CustomizeMode/FullScreenPanel/FullScreenPanel';
import type { FullScreenSection } from '@/features/experiences/components/CustomizeMode/FullScreenPanel/FullScreenPanel';
import { useCustomizeParams } from '@/features/experiences/components/CustomizeMode/useCustomizeParams';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import {
  isFeedAccessible,
  isFeedOriginDeleted,
} from '@/features/sources/lib/feed.lib';
import type { ExperienceFeed } from '@/types/gql.generated';
import { Flex, Icon, Text, Tooltip } from '@/ui';
import { AddSourceView } from './AddSourceView';
import { FeedDetail } from './FeedDetail';
import { getFeedTypeMeta } from './feedType';
import { FeedTypeIcon } from './FeedTypeIcon';

// A deleted origin is a dead end, not a pause waiting on someone's decision, so
// the rail says which of the two it is.
const pausedLabel = (feed: ExperienceFeed): string =>
  isFeedOriginDeleted(feed)
    ? 'Events paused, this experience was deleted'
    : 'Events paused, no access to this experience';

const feedToSection = (feed: ExperienceFeed): FullScreenSection => {
  const { iconType } = getFeedTypeMeta(feed.kind);
  return {
    // A source this schedule has lost access to stays in the rail, marked, so
    // the owner can see why its events stopped instead of assuming it broke.
    // The reason is the icon's accessible name as well as its tooltip: a
    // hover-only explanation reaches nobody using a screen reader.
    badge: isFeedAccessible(feed) ? undefined : (
      <Tooltip label={pausedLabel(feed)}>
        <Flex aria-label={pausedLabel(feed)} role="img">
          <Icon color="orange.500" icon="Lock" size={3.5} />
        </Flex>
      </Tooltip>
    ),
    content: () => <FeedDetail feed={feed} />,
    icon: <FeedTypeIcon iconType={iconType} />,
    id: feed.id,
    label: feed.title,
  };
};

const EMPTY_SECTION: FullScreenSection = {
  content: () => (
    <Text color="ink.muted" fontSize="ah-control">
      No sources yet. Use &ldquo;Add source&rdquo; to connect a calendar.
    </Text>
  ),
  icon: 'Rss',
  id: '__no_sources',
  label: 'No sources',
};

export const SourcesPanel = () => {
  const { experience } = usePageExperienceContext();
  const { source, openAddSource, clearSource, selectFeed } =
    useCustomizeParams();

  const feedSections = experience.feeds.map(feedToSection);
  const sections = (
    feedSections.length > 0 ? feedSections : [EMPTY_SECTION]
  ) as [FullScreenSection, ...FullScreenSection[]];

  // The `source` param drives the active panel: the add flow opens the Add-source
  // view; a feed id selects that source. Falls back to the first section (covers
  // the empty-state section when there are no feeds).
  const isAdd = source?.mode === 'add';
  const activeId =
    source?.mode === 'feed' && sections.some((s) => s.id === source.feedId)
      ? source.feedId
      : sections[0].id;

  return (
    <FullScreenPanel
      activeId={activeId}
      isActionActive={isAdd}
      sections={sections}
      header={{
        highlight: 'events',
        icon: 'Rss',
        subtitlePrefix: "Where this schedule's ",
        subtitleSuffix: ' come from.',
        title: 'Sources',
      }}
      railAction={{
        icon: 'Plus',
        label: 'Add source',
        render: (close) => <AddSourceView onCancel={close} />,
      }}
      onActivateAction={openAddSource}
      onCloseAction={clearSource}
      onSelectSection={selectFeed}
    />
  );
};
